import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"

export default class extends Controller {
  static targets = ["input", "complexity", "breach", "serverErrors",
                    "confirmationInput", "confirmationMatch", "confirmationServerErrors",
                    "submit"]
  static values  = { url: String }

  #complexityTimer      = null
  #breachTimer          = null
  #complexityPassed     = false
  #breachSafe           = false
  #confirmationMatches  = false

  connect() {
    this.#updateSubmit()
    if (this.inputTarget.value.length > 0) this.#scheduleChecks(this.inputTarget.value)
  }

  check() {
    const password = this.inputTarget.value

    clearTimeout(this.#complexityTimer)
    clearTimeout(this.#breachTimer)

    if (this.hasServerErrorsTarget) this.serverErrorsTarget.classList.add("hidden")

    if (!password) {
      this.#complexityPassed    = false
      this.#breachSafe          = false
      this.#confirmationMatches = false
      this.#updateSubmit()
      this.#clearFeedback()
      return
    }

    // Reset while in-flight so the button can't be clicked on a stale result
    this.#complexityPassed = false
    this.#breachSafe       = false
    this.#updateSubmit()

    this.#complexityTimer = setTimeout(() => this.#checkComplexity(password), 150)
    this.#breachTimer     = setTimeout(() => this.#checkBreach(password),     600)
  }

  async #checkComplexity(password) {
    const response = await patch(this.urlValue, {
      headers:      { "X-Validate": "complexity" },
      body:         { email_password_credential: { password } },
      responseKind: "json"
    })
    if (!response.ok) return
    const { rules } = await response.json
    this.#renderComplexity(rules)
  }

  async #checkBreach(password) {
    const response = await patch(this.urlValue, {
      headers:      { "X-Validate": "breach" },
      body:         { email_password_credential: { password } },
      responseKind: "json"
    })
    if (!response.ok) return
    const { breached } = await response.json
    this.#renderBreach(breached)
  }

  #renderComplexity(rules) {
    const allPassed = rules.every(r => r.passed)
    this.complexityTarget.innerHTML = rules.map(rule => `
      <li class="flex items-center gap-1.5 transition-colors ${rule.passed ? "text-green-400" : "text-hearth-300"}">
        <span class="shrink-0 w-3 text-center">${rule.passed ? "✓" : "·"}</span>
        <span>${this.#esc(rule.message)}</span>
      </li>
    `).join("")
    this.complexityTarget.classList.toggle("hidden", allPassed)
    this.#complexityPassed = allPassed
    this.#updateSubmit()
  }

  #renderBreach(breached) {
    if (breached) {
      this.breachTarget.innerHTML = `
        <p>
          This password appeared in a known data breach
          (<a href="https://haveibeenpwned.com/passwords"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-0.5 underline hover:text-red-300 transition-colors">learn
          more<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3 h-3"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg></a>)
        </p>
      `
      this.breachTarget.classList.remove("hidden")
    } else {
      this.breachTarget.classList.add("hidden")
    }
    this.#breachSafe = !breached
    this.#updateSubmit()
  }

  checkConfirmation() {
    const confirmation = this.confirmationInputTarget.value

    if (this.hasConfirmationServerErrorsTarget) {
      this.confirmationServerErrorsTarget.classList.add("hidden")
    }

    if (!confirmation) {
      this.confirmationMatchTarget.classList.add("hidden")
      this.#confirmationMatches = false
      this.#updateSubmit()
      return
    }

    const matches = confirmation === this.inputTarget.value
    this.confirmationMatchTarget.classList.toggle("hidden", matches)
    this.#confirmationMatches = matches
    this.#updateSubmit()
  }

  #updateSubmit() {
    if (!this.hasSubmitTarget) return
    const ready = this.#complexityPassed && this.#breachSafe && this.#confirmationMatches
    this.submitTarget.disabled = !ready
  }

  #scheduleChecks(password) {
    this.#complexityTimer = setTimeout(() => this.#checkComplexity(password), 150)
    this.#breachTimer     = setTimeout(() => this.#checkBreach(password),     600)
  }

  #clearFeedback() {
    this.complexityTarget.innerHTML = ""
    this.complexityTarget.classList.add("hidden")
    this.breachTarget.innerHTML = ""
    this.breachTarget.classList.add("hidden")
  }

  // Use the DOM to escape rather than manual string replacement
  #esc(str) {
    const el = document.createElement("span")
    el.textContent = str
    return el.innerHTML
  }
}
